import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { dismissToast, focusablesIn, useToasts } from '@/shared/lib'
import { Button } from '@/shared/ui/Button'
import { Toast } from '@/shared/ui/Toast'
import s from './ToastHost.module.css'

/**
 * Горячая клавиша перехода к стопке. Тост фокус не забирает никогда, поэтому до кнопки
 * «Повторить» иначе не дойти с клавиатуры — требование 4 из docs/ui-kit/contracts/toast.md.
 * Alt+T, а не F6: F6 в браузерах занят переходом в адресную строку.
 */
const FOCUS_STACK_KEY = 't'

/**
 * Хост уведомлений: портал, позиционирование стопки, общая пауза и клавиатурный доступ.
 * Ровно то, что примитив `Toast` намеренно не делает.
 *
 * Очередь живёт в `shared/lib/toast` — там же, где её видят все слои: экран зовёт
 * `showToast(t('...'))` откуда угодно, включая обработчик мутации, и о хосте не знает.
 *
 * Сам контейнер живой областью **не является**: `aria-live` стоит на каждой плашке
 * (`announce`), и вторая живая область прочитала бы каждое сообщение дважды.
 */
export function ToastHost() {
  const { t } = useTranslation()
  const toasts = useToasts()
  const stackRef = useRef<HTMLDivElement | null>(null)

  /** Курсор над стопкой морозит всю стопку, а не одну плашку: узел общий, считает он. */
  const [hovered, setHovered] = useState(false)
  /** Вкладка ушла в фон — отсчёт стоит: иначе уведомления истекут, пока их никто не видит. */
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    const sync = () => setHidden(document.hidden)
    sync()
    document.addEventListener('visibilitychange', sync)
    return () => document.removeEventListener('visibilitychange', sync)
  }, [])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.altKey || event.ctrlKey || event.metaKey) return
      if (event.key.toLowerCase() !== FOCUS_STACK_KEY) return

      const stack = stackRef.current
      if (!stack || stack.childElementCount === 0) return

      event.preventDefault()
      // Внутри плашки может не быть ни одной кнопки — тогда фокус принимает сама область
      const focusables = focusablesIn(stack)
      const first = focusables[0]
      if (first) first.focus()
      else stack.focus({ preventScroll: true })
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Пустой стопки в DOM не существует: fixed-контейнер поверх страницы не нужен ни секунды
  if (toasts.length === 0) return null

  return createPortal(
    <div
      ref={stackRef}
      className={s.root}
      // Не live-регион и не список: просто именованная область, куда уводит Alt+T
      aria-label={t('toast.regionLabel')}
      tabIndex={-1}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      {toasts.map((item) => (
        <Toast
          /* Новый id — новый узел: и отсчёт, и объявление начинаются заново */
          key={item.id}
          tone={item.tone}
          announce={item.announce}
          duration={item.duration}
          paused={hovered || hidden}
          closeLabel={t('toast.close')}
          onClose={() => dismissToast(item.id)}
          action={
            item.action ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  item.action?.onClick()
                  dismissToast(item.id)
                }}
              >
                {item.action.label}
              </Button>
            ) : undefined
          }
        >
          {item.message}
        </Toast>
      ))}
    </div>,
    document.body,
  )
}
